// src/components/superadmin/pages/PlatformFeedback.jsx
// ✅ Zero Firebase - all data via service → repo
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchAllFeedback,
  updateFeedbackStatus,
  deleteFeedback,
  bulkUpdateStatus,
  bulkDeleteFeedback,
} from '../../../services/platformFeedbackService';

import {
  Box, Container, Typography, Paper, Button, Grid,
  TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton,
  Chip, Alert, InputAdornment, Tooltip, Divider,
  Select, MenuItem, FormControl, InputLabel,
  Checkbox, Collapse, Snackbar, Skeleton,
  Menu,
} from '@mui/material';

import SearchIcon       from '@mui/icons-material/Search';
import ClearIcon        from '@mui/icons-material/Clear';
import FilterAltIcon    from '@mui/icons-material/FilterAlt';
import CloseIcon        from '@mui/icons-material/Close';
import DeleteIcon       from '@mui/icons-material/Delete';
import StarIcon         from '@mui/icons-material/Star';
import StarBorderIcon   from '@mui/icons-material/StarBorder';

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'all',         label: 'All Categories'      },
  { value: 'general',     label: 'General Experience'   },
  { value: 'ui-ux',       label: 'User Interface'       },
  { value: 'performance', label: 'System Performance'   },
  { value: 'features',    label: 'Features & Functionality' },
  { value: 'support',     label: 'Customer Support'     },
  { value: 'bug',         label: 'Bug Report'           },
];

const STATUS_OPTIONS = [
  { value: 'active',   label: 'Active',   color: 'info'      },
  { value: 'reviewed', label: 'Reviewed', color: 'warning'   },
  { value: 'resolved', label: 'Resolved', color: 'success'   },
  { value: 'archived', label: 'Archived', color: 'default'   },
];

const SORT_OPTIONS = [
  { value: 'newest',         label: 'Newest First'    },
  { value: 'oldest',         label: 'Oldest First'    },
  { value: 'highest-rating', label: 'Highest Rating'  },
  { value: 'lowest-rating',  label: 'Lowest Rating'   },
];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const getCategoryLabel = (value) =>
  CATEGORIES.find(c => c.value === value)?.label || value;

const getRatingColor = (rating) => {
  if (rating >= 4) return 'success';
  if (rating >= 3) return 'warning';
  return 'error';
};

const toSafeDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return isNaN(d) ? null : d;
};

const formatDateTime = (value) => {
  const d = toSafeDate(value);
  if (!d) return 'N/A';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const RatingStars = ({ rating }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
    {Array.from({ length: 5 }, (_, i) => (
      i < rating
        ? <StarIcon key={i} sx={{ fontSize: 16, color: '#f5a623' }} />
        : <StarBorderIcon key={i} sx={{ fontSize: 16, color: '#ccc' }} />
    ))}
  </Box>
);

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const FeedbackSkeleton = () => (
  <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Skeleton variant="text"    width={320} height={44} />
        <Skeleton variant="text"    width={400} height={20} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Skeleton variant="rounded" width={100} height={40} />
      </Box>
    </Box>

    {/* Search */}
    <Box sx={{ p: 2, borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff' }}>
      <Skeleton variant="rounded" width="40%" height={40} />
    </Box>

    {/* Table */}
    <Box sx={{ borderRadius: 3, border: '1px solid #e0e0e0', background: '#fff', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.5, borderBottom: '2px solid #e0e0e0' }}>
        {[30, 160, 100, 80, 220, 120, 80].map((w, i) => (
          <Skeleton key={i} variant="text" width={w} height={16} />
        ))}
      </Box>
      {Array.from({ length: 8 }, (_, i) => (
        <Box key={i} sx={{
          display: 'flex', gap: 2, px: 2, py: 2,
          borderBottom: '1px solid #f5f5f5', alignItems: 'center',
        }}>
          <Skeleton variant="rounded" width={20}  height={20} />
          <Box>
            <Skeleton variant="text" width={120} height={14} />
            <Skeleton variant="text" width={160} height={12} />
          </Box>
          <Skeleton variant="rounded" width={90}  height={24} />
          <Skeleton variant="rounded" width={80}  height={20} />
          <Skeleton variant="text"    width={200} height={14} />
          <Skeleton variant="text"    width={100} height={14} />
          <Skeleton variant="rounded" width={80}  height={28} />
        </Box>
      ))}
    </Box>
  </Container>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const PlatformFeedback = () => {

  // ── Data state ─────────────────────────────────────────────────────────────
  const [feedbacks,          setFeedbacks]          = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [notification,       setNotification]       = useState(null);

  // ── Selection state ────────────────────────────────────────────────────────
  const [selectedFeedbacks,  setSelectedFeedbacks]  = useState(new Set());

  // ── Filter state ───────────────────────────────────────────────────────────
  const [searchTerm,         setSearchTerm]         = useState('');
  const [categoryFilter,     setCategoryFilter]     = useState('all');
  const [sortBy,             setSortBy]             = useState('newest');
  const [showFilters,        setShowFilters]        = useState(false);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [page,               setPage]               = useState(0);
  const [rowsPerPage,        setRowsPerPage]        = useState(10);

  // ── Notification helper ────────────────────────────────────────────────────
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  // ── Load data via service → repo ───────────────────────────────────────────
  const loadFeedback = useCallback(async () => {
    setLoading(true);
    try {
      // ✅ Goes: component → service → repo → (Supabase or Firebase)
      const data = await fetchAllFeedback();
      const normalised = data.map(fb => ({
        ...fb,
        timestamp: toSafeDate(fb.timestamp) || new Date(),
      }));
      setFeedbacks(normalised);
    } catch (err) {
      console.error('[PlatformFeedback] loadFeedback error:', err);
      showNotification('Failed to load feedback data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  // ── Filter + sort pipeline ─────────────────────────────────────────────────
  const filteredFeedbacks = useMemo(() => {
    let result = [...feedbacks];

    // Category
    if (categoryFilter !== 'all') {
      result = result.filter(fb => fb.category === categoryFilter);
    }

    // Search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(fb =>
        fb.userName?.toLowerCase().includes(term) ||
        fb.userEmail?.toLowerCase().includes(term) ||
        fb.comment?.toLowerCase().includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':         return b.timestamp - a.timestamp;
        case 'oldest':         return a.timestamp - b.timestamp;
        case 'highest-rating': return b.rating - a.rating;
        case 'lowest-rating':  return a.rating - b.rating;
        default:               return 0;
      }
    });

    return result;
  }, [feedbacks, categoryFilter, searchTerm, sortBy]);

  // ── Reset filters ──────────────────────────────────────────────────────────
  const resetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setSortBy('newest');
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const currentPageIds = filteredFeedbacks
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map(fb => fb.id);

  const toggleSelect = (id) => {
    const next = new Set(selectedFeedbacks);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedFeedbacks(next);
  };

  const toggleSelectAllPage = (checked) => {
    const next = new Set(selectedFeedbacks);
    currentPageIds.forEach(id => checked ? next.add(id) : next.delete(id));
    setSelectedFeedbacks(next);
  };

  const allOnPageSelected =
    currentPageIds.length > 0 &&
    currentPageIds.every(id => selectedFeedbacks.has(id));

  // ── CRUD handlers via service → repo ───────────────────────────────────────

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    try {
      // ✅ Goes: component → service → repo
      await updateFeedbackStatus(feedbackId, newStatus);
      setFeedbacks(prev =>
        prev.map(fb => fb.id === feedbackId ? { ...fb, status: newStatus } : fb)
      );
      showNotification(`Status updated to ${newStatus}`);
    } catch (err) {
      showNotification('Failed to update status', 'error');
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedFeedbacks.size === 0) return;
    try {
      await bulkUpdateStatus([...selectedFeedbacks], newStatus);
      setFeedbacks(prev =>
        prev.map(fb => selectedFeedbacks.has(fb.id) ? { ...fb, status: newStatus } : fb)
      );
      setSelectedFeedbacks(new Set());
      showNotification(`${selectedFeedbacks.size} items updated to ${newStatus}`);
    } catch (err) {
      showNotification('Bulk update failed', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedFeedbacks.size} feedback items?`)) return;
    try {
      await bulkDeleteFeedback([...selectedFeedbacks]);
      setFeedbacks(prev => prev.filter(fb => !selectedFeedbacks.has(fb.id)));
      setSelectedFeedbacks(new Set());
      showNotification('Selected feedback deleted');
    } catch (err) {
      showNotification('Bulk delete failed', 'error');
    }
  };

  const handleChangePage        = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  // ── Guard ──────────────────────────────────────────────────────────────────
  if (loading) return <FeedbackSkeleton />;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1"
            sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.5px' }}>
            Platform Feedback Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View, manage, and analyse user feedback across the platform
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<FilterAltIcon />}
            onClick={() => setShowFilters(v => !v)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {showFilters ? 'Hide Filters' : 'Filters'}
          </Button>
        </Box>
      </Box>

      {/* ── SNACKBAR ── */}
      <Snackbar open={!!notification} autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={notification?.type || 'success'}
          onClose={() => setNotification(null)}>
          {notification?.message}
        </Alert>
      </Snackbar>

      {/* ── SEARCH BAR (always visible) ── */}
      <Paper sx={{ p: 2, borderRadius: 3 }} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" label="Search Feedback"
              placeholder="Search by name, email, comment..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'action.active' }} fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          {searchTerm && (
            <Grid item xs={12} md={8}>
              <Typography variant="body2" color="text.secondary">
                Found <strong>{filteredFeedbacks.length}</strong>{' '}
                feedback{filteredFeedbacks.length !== 1 ? 's' : ''} matching "{searchTerm}"
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ── COLLAPSIBLE FILTERS ── */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2.5, borderRadius: 3 }} variant="outlined">
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} label="Category"
                  onChange={e => setCategoryFilter(e.target.value)}
                  sx={{ borderRadius: 2 }}>
                  {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select value={sortBy} label="Sort By"
                  onChange={e => setSortBy(e.target.value)}
                  sx={{ borderRadius: 2 }}>
                  {SORT_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}
              sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Button variant="text" color="info" onClick={resetFilters}
                startIcon={<ClearIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                Clear Filters
              </Button>
              <Tooltip title="Close filters" arrow>
                <IconButton size="small" onClick={() => setShowFilters(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* ── BULK ACTIONS BAR ── */}
      {selectedFeedbacks.size > 0 && (
        <Paper variant="outlined" sx={{
          p: 1.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2,
          bgcolor: 'rgba(21,101,192,0.04)',
        }}>
          <Typography variant="body2" fontWeight={600}>
            {selectedFeedbacks.size} selected
          </Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select displayEmpty defaultValue=""
              onChange={e => { if (e.target.value) handleBulkStatusUpdate(e.target.value); }}
              sx={{ borderRadius: 2 }}>
              <MenuItem value="" disabled>Update Status</MenuItem>
              {STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" color="error" size="small"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Delete Selected
          </Button>
          <Button variant="text" size="small"
            onClick={() => setSelectedFeedbacks(new Set())}
            sx={{ textTransform: 'none', fontWeight: 600, ml: 'auto' }}>
            Clear Selection
          </Button>
        </Paper>
      )}

      {/* ── TABLE ── */}
      <TableContainer component={Paper} variant="outlined" sx={{
        borderRadius: 3,
        boxShadow: '0px 4px 16px rgba(0,0,0,0.02)',
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 8 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.12)', borderRadius: 4 },
      }}>
        {filteredFeedbacks.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Alert severity="info" sx={{ justifyContent: 'center', borderRadius: 2.5 }}>
              {feedbacks.length === 0 ? 'No feedback submitted yet' : 'No feedback matches your filters'}
            </Alert>
            {feedbacks.length > 0 && (
              <Button onClick={resetFilters} sx={{ mt: 2, textTransform: 'none' }}>
                Clear Filters
              </Button>
            )}
          </Box>
        ) : (
          <Table sx={{ minWidth: 1000 }} size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ bgcolor: 'background.neutral' }}>
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={selectedFeedbacks.size > 0 && !allOnPageSelected}
                    onChange={e => toggleSelectAllPage(e.target.checked)}
                  />
                </TableCell>
                {['Sl.No', 'User', 'Category', 'Rating', 'Comment', 'Date', 'Status'].map((h, i) => (
                  <TableCell key={i}
                    align={h === 'Sl.No' ? 'center' : 'left'}
                    sx={{ fontWeight: 700, bgcolor: 'background.neutral', color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredFeedbacks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((fb, index) => {
                  const isSelected = selectedFeedbacks.has(fb.id);
                  return (
                    <TableRow key={fb.id} hover selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} onChange={() => toggleSelect(fb.id)} />
                      </TableCell>

                      {/* Sl.No */}
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>

                      {/* User */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {fb.userName || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {fb.userEmail || '—'}
                        </Typography>
                      </TableCell>

                      {/* Category */}
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          label={getCategoryLabel(fb.category)}
                          sx={{ fontWeight: 600, fontSize: '0.72rem' }} />
                      </TableCell>

                      {/* Rating */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <RatingStars rating={fb.rating || 0} />
                          <Chip size="small" label={fb.rating || 0}
                            color={getRatingColor(fb.rating)}
                            sx={{ fontWeight: 700, fontSize: '0.7rem', minWidth: 28 }} />
                        </Box>
                      </TableCell>

                      {/* Comment */}
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip title={fb.comment || 'No comment'} arrow
                          disableHoverListener={!fb.comment || fb.comment.length <= 50}>
                          <Typography variant="body2" color="text.secondary" sx={{
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', maxWidth: 200,
                          }}>
                            {fb.comment || <em style={{ color: '#999' }}>No comment</em>}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(fb.timestamp)}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Select size="small" value={fb.status || 'active'}
                          onChange={e => handleStatusUpdate(fb.id, e.target.value)}
                          sx={{ borderRadius: 2, fontSize: '0.78rem', minWidth: 100 }}>
                          {STATUS_OPTIONS.map(s => (
                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* ── PAGINATION ── */}
      {filteredFeedbacks.length > 0 && (
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0,
        }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            Showing {page * rowsPerPage + 1} to{' '}
            {Math.min((page + 1) * rowsPerPage, filteredFeedbacks.length)} of{' '}
            {filteredFeedbacks.length} feedback
          </Typography>
          <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div"
            count={filteredFeedbacks.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: 'none', overflow: 'hidden' }}
          />
        </Box>
      )}

    </Container>
  );
};

export default PlatformFeedback;